/**
 * R-6.5 Phase 0: i18n foundation for APAC multi-language support.
 *
 * 13 locales × 20 namespaces, all bundled at build time via Vite's
 * `import.meta.glob`. Language detection order: localStorage
 * (`vynaemr-language` key) → browser navigator → <html lang>.
 *
 * Add a locale: drop a directory `./locales/<lang>/` with the 20
 * namespace JSONs, then add to SUPPORTED_LANGUAGES.
 *
 * Add a namespace: bump NAMESPACES + create `<lang>/<ns>.json` in
 * every locale dir. The locale-coverage drift gate enforces parity.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { syncDateFnsLocale } from '../lib/dateLocale'

export const NAMESPACES = [
  'common', 'nav', 'auth', 'patient', 'encounter', 'orders',
  'results', 'medications', 'notes', 'scheduling', 'billing',
  'portal', 'admin-app', 'fhir', 'interfaces',
  'specimen', 'handoff', 'flowsheets', 'smartphrases',
  'analytics', 'errors', 'inbox',
] as const
export type Namespace = (typeof NAMESPACES)[number]

export const SUPPORTED_LANGUAGES = [
  { code: 'en',    label: 'English' },
  { code: 'en-SG', label: 'English (SG)' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'zh-HK', label: '繁體中文 (香港)' },
  { code: 'ja',    label: '日本語' },
  { code: 'ko',    label: '한국어' },
  { code: 'vi',    label: 'Tiếng Việt' },
  { code: 'th',    label: 'ไทย' },
  { code: 'id',    label: 'Bahasa Indonesia' },
  { code: 'ms',    label: 'Bahasa Melayu' },
  { code: 'hi',    label: 'हिन्दी' },
  { code: 'ta',    label: 'தமிழ்' },
] as const
export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

// Build-time glob: `./locales/<lang>/<ns>.json` (excluding `_template.json`)
const modules = import.meta.glob('./locales/*/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>

const resources: Record<string, Record<string, Record<string, unknown>>> = {}
for (const [path, mod] of Object.entries(modules)) {
  const m = path.match(/\.\/locales\/([^/]+)\/([^.]+)\.json/)
  if (!m) continue
  const [, lang, ns] = m
  if (ns === '_template') continue
  resources[lang] ??= {}
  resources[lang][ns] = mod.default
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    debug: import.meta.env.DEV,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'vynaemr-language',
    },
  })

// Month/day names follow the UI language (date-fns global default);
// without this every format() renders English regardless of locale.
syncDateFnsLocale(i18n.language)
i18n.on('languageChanged', syncDateFnsLocale)

export default i18n
